import { useEffect, useRef, useState } from "react";
import { AlertCircle, Instagram } from "lucide-react";
import { resolveAssetUrl } from "@/lib/assetUrl";

/**
 * Renders an Instagram post/reel inline using the official embed script.
 *
 * Falls back gracefully when the post is private, deleted, or when
 * Instagram's script throttles/blocks: we show the pin's poster image
 * (already stored in our object storage), the caption, and a small
 * "Content unavailable on Instagram" chip so the user isn't dumped on a
 * broken tile.
 *
 * Analytics: fires `drop_video_play` once the embed successfully renders
 * (detected via the IG script mutating our blockquote into an iframe).
 */
const IG_SCRIPT_SRC = "//www.instagram.com/embed.js";
const RENDER_TIMEOUT_MS = 4500;

let scriptPromise = null;
function loadInstagramScript() {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.instgrm?.Embeds?.process) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve) => {
    const existing = document.querySelector(`script[src="${IG_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", resolve, { once: true });
      existing.addEventListener("error", resolve, { once: true });
      return;
    }
    const s = document.createElement("script");
    s.async = true;
    s.src = IG_SCRIPT_SRC;
    s.onload = () => resolve();
    s.onerror = () => resolve();
    document.body.appendChild(s);
  });
  return scriptPromise;
}

export default function InstagramEmbedPlayer({
  sourceUrl,
  posterUrl,
  title,
  caption,
  onRender,
}) {
  const wrapRef = useRef(null);
  const timerRef = useRef(null);
  const [renderState, setRenderState] = useState("loading"); // loading|ready|failed

  useEffect(() => {
    if (!sourceUrl) return;
    let cancelled = false;
    setRenderState("loading");

    // Build the Instagram blockquote target via the DOM API rather than
    // dangerouslySetInnerHTML — the IG script mutates this node itself.
    if (wrapRef.current) {
      wrapRef.current.innerHTML = "";
      const bq = document.createElement("blockquote");
      bq.className = "instagram-media";
      bq.setAttribute("data-instgrm-permalink", sourceUrl);
      bq.setAttribute("data-instgrm-version", "14");
      bq.style.background = "#000";
      bq.style.margin = "0";
      bq.style.padding = "0";
      bq.style.width = "100%";
      wrapRef.current.appendChild(bq);
    }

    loadInstagramScript().then(() => {
      if (cancelled) return;
      // Give the script a tick to attach itself to the DOM.
      requestAnimationFrame(() => {
        try {
          window.instgrm?.Embeds?.process();
        } catch {
          /* ignore */
        }
      });

      // Detect success: the IG script replaces the blockquote's contents
      // with a rendered iframe. Poll briefly, then bail out.
      const start = Date.now();
      const check = () => {
        if (cancelled) return;
        const iframe = wrapRef.current?.querySelector("iframe");
        if (iframe) {
          setRenderState("ready");
          onRender?.();
          return;
        }
        if (Date.now() - start >= RENDER_TIMEOUT_MS) {
          setRenderState("failed");
          return;
        }
        timerRef.current = window.setTimeout(check, 250);
      };
      timerRef.current = window.setTimeout(check, 400);
    });

    return () => {
      cancelled = true;
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [sourceUrl, onRender]);

  if (!sourceUrl) return null;

  return (
    <div
      className="relative w-full rounded-2xl overflow-hidden bg-black/40 border border-white/5"
      data-testid="ig-embed-wrap"
      data-render-state={renderState}
    >
      {/* Poster shown while loading and permanently if IG can't render. */}
      {renderState !== "ready" && (
        <div className="relative">
          <img
            src={resolveAssetUrl(posterUrl)}
            alt={title || "Instagram drop"}
            className="w-full h-auto object-cover"
            data-testid="ig-embed-poster"
          />
          {renderState === "failed" && (
            <div
              className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-4"
              data-testid="ig-embed-fallback"
            >
              <p className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-white/80 bg-white/10 backdrop-blur-sm rounded-full px-3 py-1">
                <AlertCircle className="w-3.5 h-3.5" />
                Content unavailable on Instagram
              </p>
              {caption && (
                <p className="text-white/85 text-sm mt-3 line-clamp-3 leading-snug">
                  {caption}
                </p>
              )}
            </div>
          )}
          {renderState === "loading" && (
            <div className="absolute top-3 left-3 inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/80 bg-black/50 backdrop-blur-sm rounded-full px-2.5 py-1">
              <Instagram className="w-3 h-3" />
              Loading reel…
            </div>
          )}
        </div>
      )}

      {/* Instagram embed target — the useEffect above appends a blockquote
          via DOM API so the IG script can mutate it into an iframe. */}
      <div
        ref={wrapRef}
        className={renderState === "ready" ? "block" : "hidden"}
        data-testid="ig-embed-slot"
      />
    </div>
  );
}
